import { useContext, useEffect, useState } from "react";
import { Button, Checkbox, Flex, Form, Input, Tooltip } from "antd";
import { Combo, ComboFetchFuncParameters, NumericInput, } from "../../../core/component";
import { CloseOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";
import { translate } from "../../../core";
import { resolveNumber } from "../../../core/until";
import styles from "./Order.module.scss";
import { Item, ItemType } from "../../inventory/type";
import { Order, OrderEntry, OrderLine, OrderLineEntry, OrderRepair, } from "../type";
import { mapEntryLine } from "../model-mapper/order-map-entity-to-entry";
import {
  getAvailableInStockQuantity,
  isReturnQuantity,
  resolveTakeWithAndBackOrderQuantities,
} from "../method/order-line-availability";
import { PageContextValue, } from "../../../core/type";
import { OrderPageContext } from "./OrderPageContext";
import { WeddingRegistry } from "../../wedding-registry/type";
import {
  weddingCalculatePrice,
  weddingReformOrderPrices,
} from "../../wedding-registry/method/wedding-registry-pricing-order";
import { getOrderPrice } from "../method/order-pricing";
import { ITEM_NUMBERS_NOT_HAVING_EFFECTS } from "../../../App.constants";
import { useLazyQuery } from "@apollo/client";
import { GetItemsForLookup } from "../../inventory/graphql/item.graphql";
import { OrderFormRepairCheckBox } from "./OrderFormRepairCheckBox";
import { initNewRepairLine } from "../method/initNewRepairLine";
import { InputCard } from "../../inventory/component/InputCard";
import { OrderFormLineRow, OrderFormLinesSortable } from "./OrderFormLinesSortable";
import { NamePath } from 'antd/es/form/interface'

interface WeddingRegistryEntryLinesProps {
  onAmountsChanged: () => void;
  onSelectRepair: (version: string) => void;
  id?: number;
  weddingRegistry: WeddingRegistry | null | undefined;
}

export const OrderFormLines = ({
  onAmountsChanged,
  onSelectRepair,
  weddingRegistry,
}: WeddingRegistryEntryLinesProps) => {
  const { form, entity } =
    useContext<PageContextValue<Order, OrderEntry>>(OrderPageContext);
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);
  const [itemsFind] = useLazyQuery(GetItemsForLookup);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    const entry = form?.getFieldsValue();
    const lines = entry?.lines;

    if (!(lines && lines.length > 0)) return;

    if (!weddingRegistry) return;

    weddingReformOrderPrices(weddingRegistry, lines);
    form?.setFieldsValue(entry as any);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weddingRegistry]);
  const itemFetch = async ({
    request,
    value,
    search,
  }: ComboFetchFuncParameters<number>) => {
    const { data } = await itemsFind({
      variables: {
        request: {
          search,
          id: value,
          take: request?.take ?? 10,
          skip: request?.skip ?? 0,
          showNoEffects: true,
          archived: false,
        },
      },
    });

    const list = data?.itemsFind.data ?? [];

    return list.map((e) => ({
      value: e.id || 0,
      display: e.sku,
      item: e || {},
    }));
  };

  const removeRepair = (version: string) => {
    const repairs = (form?.getFieldValue("repairs") ?? []) as OrderRepair[];
    form?.setFieldValue(
      "repairs",
      repairs.filter((r) => r.version !== version)
    );
  };
  const onIsRepairChanged = (index: number, checked: boolean) => {
    const line = form?.getFieldValue(["lines", index]) as OrderLineEntry;

    if (!checked) {
      form?.setFieldValue(["lines", index, "repairVersion"], null);
      removeRepair(line.repairVersion);
      setForceUpdate(true);
      return;
    }

    if (!line.repairVersion) {
      initNewRepairLine(form, line);

      form?.setFieldValue(
        ["lines", index, "repairVersion"],
        line.repairVersion
      );
      form?.setFieldValue(["lines", index, "description"], line.description);
      form?.setFieldValue(["lines", index, "quantity"], line.quantity);
      form?.setFieldValue(["lines", index, "unitPrice"], line.unitPrice);
      form?.setFieldValue(
        ["lines", index, "takeWithQuantity"],
        line.takeWithQuantity
      );
    }

    onSelectRepair(line.repairVersion);
    onAmountsChanged();
  };

  const onSelectItem = (index: number) => (_: any, item: Item) => {
    const { price, onSale } = calculatePrice(item);
    const fieldPath: NamePath<OrderEntry> = ["lines", index];

    const description = [ItemType.Tableware, ItemType.Giftware].includes(
      item.type
    )
      ? `${item.description.trim()} / ${item.style}`
      : item.description;

    form?.setFieldValue([...fieldPath, "item"], item);
    form?.setFieldValue([...fieldPath, "description"], description);
    form?.setFieldValue([...fieldPath, "unitPrice"], price);
    form?.setFieldValue([...fieldPath, "onSale"], onSale);
    form?.setFieldValue([...fieldPath, "taxable"], true);
    form?.setFieldValue([...fieldPath, "sku"], item.sku);

    onLineChanged(index, true);
  };

  const calculatePrice = (item: Item): { price: number; onSale: boolean } => {
    const fromWeddingRegistry = weddingCalculatePrice(
      item.id,
      weddingRegistry as any
    );

    if (fromWeddingRegistry)
      return { price: fromWeddingRegistry, onSale: false };

    return getOrderPrice(item);
  };

  const onLineChanged = (index: number, recalculateAllocation = false) => {
    const line: OrderLineEntry = form?.getFieldValue(["lines", index]);
    mapEntryLine(line, entity?.taxRate as number);
    form?.setFieldValue(["lines", index], line);

    if (recalculateAllocation) {
      calculateTakeWithAndBackOrderQuantityFields(index);
    }
    onAmountsChanged();
  };

  const onTakeWithChanged = (index: number) => {
    calculateTakeWithAndBackOrderQuantityFields(index, true);
  };

  const calculateTakeWithAndBackOrderQuantityFields = (
    index: number,
    fromTakeWithInput?: boolean
  ) => {
    const field: NamePath<OrderEntry> = ["lines", index];
    const row = form?.getFieldValue(field) as OrderLineEntry;

    if (
      !isReturnQuantity(row.quantity) &&
      (!row.sku || ITEM_NUMBERS_NOT_HAVING_EFFECTS.includes(row.sku))
    )
      return;

    const { takeWithQuantity, backOrderQuantity } =
      resolveTakeWithAndBackOrderQuantities(
        row,
        fromTakeWithInput ? row.takeWithQuantity : null
      );

    form?.setFieldValue([...field, "takeWithQuantity"], takeWithQuantity);
    form?.setFieldValue([...field, "backOrderQuantity"], backOrderQuantity);
  };

  useEffect(() => {
    setForceUpdate(false);
  }, [forceUpdate]);

  return (
    <InputCard
      contained
      title={translate("items")}
      expandable={true}
      onExpand={() => setIsExpanded(!isExpanded)}
    >
      {isExpanded && (
        <Form.List name="lines">
          {(fields, { add, remove, move }) => (
            <OrderFormLinesSortable
              ids={fields.map((field) => String(field.key))}
              onMove={move}
            >
              <Form.Item style={{ marginBottom: "0", marginTop: "10px" }}>
                <Button
                  onClick={() => add({ quantity: 1 })}
                  icon={<PlusOutlined />}
                  className="btn-primary-gradient"
                >
                  {translate("add", "items")}
                </Button>
              </Form.Item>

              <table className={`table mb-2 ${styles.orderLinesTable}`}>
                <thead>
                  <tr>
                    <th className={styles.orderLineDragColumn}></th>
                    <th className="text-center f-medium" style={{ width: "5%" }}>
                      {translate("repair_abbr")}
                    </th>
                    <th className="text-center f-medium" style={{ width: "12%" }}>
                      {translate("item_number_abbr")}
                    </th>
                    <th className="text-center f-medium" style={{ width: "8%" }}>
                      {translate("quantity_abbr")}
                    </th>
                    <th className={`text-center f-medium ${styles.orderLineDescriptionColumn}`}>
                      {translate("description")}
                    </th>
                    <th className={`text-center f-medium ${styles.orderLineTakeWithColumn}`}>
                      {translate("take_with_abbr")}
                    </th>
                    <th className="text-center f-medium" style={{ width: "5%" }}>
                      {translate("back_order_abbr")}
                    </th>
                    <th className={`text-center f-medium ${styles.orderLineAmountColumn}`}>
                      {translate("unit_price")}
                    </th>
                    <th className={`text-center f-medium ${styles.orderLineAmountColumn}`}>
                      {translate("extended")}
                    </th>
                    <th className={`text-center f-medium ${styles.orderLineAmountColumn}`}>
                      {translate("tax")}
                    </th>
                    <th className="text-center f-medium" style={{ width: "5%" }}>
                      {translate("taxable")}
                    </th>
                    <th className="text-center f-medium" style={{ width: "3%" }}></th>
                  </tr>
                </thead>

                <tbody className={styles["orderLines"]}>
                  {fields.map((field, index) => (
                    <Form.Item
                      key={field.key}
                      noStyle
                      shouldUpdate
                    >
                      {({ getFieldValue }) => {
                        const { archived } = (getFieldValue(['lines', field.name, 'item']) ?? {}) as Item
                        const line = form?.getFieldValue(['lines', index]) as OrderLine

                        // isListField keeps each field with its row when the row moves. Without it a field
                        // is keyed by its index and remounts on a reorder, dropping the item lookup.
                        return (
                          <OrderFormLineRow
                            id={String(field.key)}
                            disabled={fields.length < 2}
                          >
                            <td className="" style={{ height: '65px' }}>
                              <Flex
                                gap={10}
                                align="center"
                              >
                                <Form.Item
                                  isListField
                                  valuePropName="checked"
                                  name={[field.name, "isRepair"]}
                                >
                                  <OrderFormRepairCheckBox
                                    disabled={archived}
                                    onChange={(checked) =>
                                      onIsRepairChanged(index, !!checked)
                                    }
                                  />
                                </Form.Item>

                                <Form.Item
                                  shouldUpdate
                                  noStyle
                                  style={{ flex: 1 }}
                                >
                                  {({ getFieldValue }) => {
                                    const line = getFieldValue(['lines', field.name]) as OrderLine

                                    if (!line.isRepair)
                                      return null

                                    return (
                                      <Flex justify="center" style={{ width: '100%' }}>
                                        <Form.Item isListField name={[field.name, "repairVersion"]} noStyle>
                                          <Button
                                            className={styles.orderRepairVersionButton}
                                            shape="circle"
                                            type="text"
                                            onClick={() => {
                                              const line = form?.getFieldValue([
                                                "lines",
                                                index,
                                              ]) as OrderLine;
                                              onSelectRepair(line?.repairVersion);
                                            }}
                                          >
                                            {line.repairVersion}
                                          </Button>
                                        </Form.Item>
                                      </Flex>
                                    )
                                  }}

                                </Form.Item>
                              </Flex>

                            </td>

                            <td>
                              {
                                archived
                                  ? <Input
                                    disabled
                                    value={line.item.sku}
                                  />
                                  : <Form.Item
                                    isListField
                                    name={[field.name, "item", "id"]}
                                    className={styles["orderLineItemNumber"]}
                                  >
                                    <Combo<Item, number>
                                      size="middle"
                                      fetch={itemFetch}
                                      onSelect={onSelectItem(index)}
                                    />
                                  </Form.Item>
                              }
                            </td>

                            <td>
                              <Form.Item
                                isListField
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      "missing_field_message",
                                      "quantity_abbr"
                                    ),
                                  },
                                ]}
                                name={[field.name, "quantity"]}
                              >
                                <NumericInput
                                  disabled={archived}
                                  className={`text-center`}
                                  onChange={() => onLineChanged(index, true)}
                                  decimalPlaces={0}
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item
                                isListField
                                noStyle
                                name={[field.name, "description"]}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      "missing_field_message",
                                      "description"
                                    ),
                                  },
                                ]}
                              >
                                <Input
                                  disabled={archived}
                                />
                              </Form.Item>
                            </td>

                            <td className={styles.orderLineTakeWith}>
                              <Form.Item
                                noStyle
                                shouldUpdate
                              >
                                {({ getFieldValue }) => {
                                  const line = getFieldValue(['lines', field.name]) as OrderLineEntry

                                  const availableInStockQuantity = getAvailableInStockQuantity(line)
                                  const { takeWithQuantity: reachableTakeWithQuantity } =
                                    resolveTakeWithAndBackOrderQuantities(line)

                                  const showWarning = resolveNumber(line.takeWithQuantity) < reachableTakeWithQuantity
                                  const warningText = showWarning ? translate('available_quantity_is', availableInStockQuantity.toString(), ',', 'use_take_wity_quantity_warning') : ''

                                  return <Tooltip title={warningText} color='orange'>
                                    <Form.Item
                                      isListField
                                      name={[field.name, "takeWithQuantity"]}
                                    >
                                      <NumericInput
                                        style={{ height: 49 }}
                                        disabled={archived}
                                        className={`${styles.readOnlyInput} text-center`}
                                        onChange={() => onTakeWithChanged(index)}
                                        decimalPlaces={0}
                                        suffix={!showWarning
                                          ? <></>
                                          : <WarningOutlined style={{ color: "orange" }} />}
                                      />
                                    </Form.Item>
                                  </Tooltip>
                                }}
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item isListField name={[field.name, "backOrderQuantity"]}>
                                <NumericInput
                                  disabled
                                  className={`${styles.readOnlyInput} text-center`}
                                  decimalPlaces={0}
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item
                                isListField
                                name={[field.name, "unitPrice"]}
                                rules={[
                                  {
                                    required: true,
                                    message: translate(
                                      "missing_field_message",
                                      "unit_price"
                                    ),
                                  },
                                ]}
                              >
                                <NumericInput
                                  disabled={archived}
                                  className={styles.amountInput}
                                  onChange={() => onLineChanged(index)}
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item isListField name={[field.name, "extended"]}>
                                <NumericInput
                                  className={`${styles.readOnlyInput} ${styles.amountInput}`}
                                  disabled
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item isListField name={[field.name, "tax"]}>
                                <NumericInput
                                  className={`${styles.readOnlyInput} ${styles.amountInput}`}
                                  disabled
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Form.Item
                                isListField
                                name={[field.name, "taxable"]}
                                valuePropName="checked"
                              >
                                <Checkbox
                                  disabled={archived}
                                  type="checkbox"
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                  onChange={() => onLineChanged(index)}
                                />
                              </Form.Item>
                            </td>

                            <td>
                              <Tooltip title={translate("remove")}>
                                <Button
                                  disabled={archived}
                                  onClick={() => {
                                    remove(index)
                                    line.repairVersion && removeRepair(line.repairVersion);
                                    setTimeout(() => {
                                      onAmountsChanged();
                                    }, 100);
                                  }}
                                  type="text"
                                  shape="circle"
                                  icon={<CloseOutlined />}
                                />
                              </Tooltip>
                            </td>
                          </OrderFormLineRow>
                        )
                      }}
                    </Form.Item>
                  ))}
                </tbody>
              </table>
            </OrderFormLinesSortable>
          )}
        </Form.List>
      )}
    </InputCard>
  );
};
